import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isMenuRouteAllowed, firstGrantedPath } from '@/lib/menuPermissions';
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { TopNavigation } from './TopNavigation';
import { useIsMobile } from '@/hooks/use-mobile';
import BillingDialog from './FreeTrailSteps/BillingDialog';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { TrialBanner } from '../trial/TrialBanner';
import { WabaLockedBanner } from '../WabaLockedBanner';
import { MetaTokenExpiryBanner } from '../MetaTokenExpiryBanner';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { AccountScopeBoundary } from './AccountScopeBoundary';
import { useAdsSetupStatus } from '@/hooks/useAds';
import { ConnectMetaBanner } from '@/components/reusable/ConnectMetaBanner';
import { ConnectMetaModal } from '@/components/business-account/ConnectMetaModal';

import { useAuth } from '@/components/AuthProvider';
import { useInboxSoundNotification } from '@/hooks/useInboxSoundNotification';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useSidebarTour } from '@/hooks/useSidebarTour';
import { OnboardingChecklist } from '../Onboarding/OnboardingChecklist';
import { SubscriptionGuardProvider } from '@/contexts/SubscriptionGuardContext';
import { AiChatProvider } from '@/contexts/AiChatContext';
import { ChatWidget } from './ChatWidget/ChatWidget';
import { GenUIProvider, useGenUI, GenUIChatbox } from '@/components/genui';
import { GenUIProvider as GenUIFullProvider } from '@/contexts/GenUIContext';
import { PushNotificationManager } from '../PushNotificationManager';
import { PWAInstallPrompt } from '../PWAInstallPrompt';
import { WhatsNewModal } from '../WhatsNewModal';
import { setBlockingDialog } from '@/lib/blockingDialogs';
import { cn } from "@/lib/utils";
import { isTrialActuallyExpired as checkTrialExpired, isSubscriptionActive, isSubscriptionExpiredOrCancelled } from "@/lib/subscription-utils";
// WhatsApp calling feature disabled
// import { WhatsAppCallDialer } from '../whatsapp/WhatsAppCallDialer';
// import { IncomingCallDialog } from '../whatsapp/IncomingCallDialog';
// import { useWhatsAppCall } from '@/hooks/useWhatsAppCall';
// import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
// import { Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
// import {
//   Dialog,
//   DialogContent,
//   DialogTrigger,
// } from "@/components/ui/dialog";

interface AppShellProps {
  children: React.ReactNode;
}

const AppShellContent: React.FC<AppShellProps> = ({ children }) => {
  const { state, setOpen } = useSidebar();
  const location = useLocation();
  const isMobile = useIsMobile();
  const initializedPathnameRef = React.useRef<string | null>(null);
  const [showBillingDialog, setShowBillingDialog] = React.useState(false);
  const { user, loading: authLoading } = useAuth();
  const { isOpen: isGenUIOpen } = useGenUI();
  const isGenUIMode = isGenUIOpen;

  // Auto-collapse sidebar when GenUI mode is active
  const prevGenUIMode = React.useRef(isGenUIMode);
  const prevSidebarState = React.useRef<string>(state);
  React.useEffect(() => {
    if (isGenUIMode && !prevGenUIMode.current) {
      // Entering GenUI mode — remember sidebar state and collapse
      prevSidebarState.current = state;
      setOpen(false);
    } else if (!isGenUIMode && prevGenUIMode.current) {
      // Leaving GenUI mode — restore previous sidebar state
      if (prevSidebarState.current === 'expanded') {
        setOpen(true);
      }
    }
    prevGenUIMode.current = isGenUIMode;
  }, [isGenUIMode]);

  // Determine user role and flags
  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;
  const isRegularUser = !isSuperAdmin && !isAgency;

  // Handle sidebar state when navigating between pages
  // Only update if pathname changes (not when state changes from manual toggle)
  const previousPathnameRef = React.useRef<string>(location.pathname);

  // No secondary menu logic needed


  const organizationId = user?.organization_id;

  // Check current subscription status (only for regular users). Uses the shared
  // hook so this component + AppSidebar + TopNavigation + SubscriptionGuard +
  // SubscriptionGuardContext + BillingDialog all share a single cached fetch.
  const { data: currentSubscription, isLoading: isSubscriptionLoading, isError: isSubscriptionError } = useCurrentSubscription({
    enabled: !!organizationId && !authLoading && isRegularUser,
    staleTime: 5 * 60 * 1000, // AppShell is the outermost owner — keep its longer 5m TTL.
  });

  // Track whether we've already auto-opened billing dialog for this session
  // to avoid re-opening after user dismisses it
  const hasAutoOpenedBillingRef = React.useRef(false);

  // Check if billing dialog should be shown (only for regular users)
  React.useEffect(() => {
    if (!isRegularUser || typeof window === 'undefined' || !organizationId || authLoading) return;

    // First, check if user has a subscription (active, trialing, or pending)
    // If yes, don't show dialog and clear any localStorage flags
    if (currentSubscription !== undefined) {
      if (isSubscriptionActive(currentSubscription)) {
        setShowBillingDialog(false);
        localStorage.removeItem('showBillingDialog');
        hasAutoOpenedBillingRef.current = false;
        return;
      }

      const isMainPage = location.pathname.startsWith('/home') &&
        !location.pathname.startsWith('/home/admin') &&
        !location.pathname.startsWith('/home/agency');

      const isExpiredOrCancelled = isSubscriptionExpiredOrCancelled(currentSubscription);

      if (isExpiredOrCancelled && isMainPage && !showBillingDialog && !hasAutoOpenedBillingRef.current) {
        hasAutoOpenedBillingRef.current = true;
        localStorage.removeItem('showBillingDialog');
        setShowBillingDialog(true);
        return;
      }

      // No subscription at all (null) or localStorage flag set — check localStorage
      const shouldShowFromStorage = localStorage.getItem('showBillingDialog');

      if (shouldShowFromStorage === 'true') {
        localStorage.removeItem('showBillingDialog');
        if (isMainPage && !showBillingDialog) {
          setShowBillingDialog(true);
        }
      }
    }
  }, [location.pathname, currentSubscription, organizationId, showBillingDialog, authLoading, isRegularUser]);

  // On mobile, sidebar is handled by Sheet component, so no margin needed
  // On desktop, always use collapsed width (icon only)
  // If secondary sidebar is visible, add its width (240px) to the margin
  const secondarySidebarWidth = 0;
  const currentSidebarWidth = state === 'expanded' ? 'var(--sidebar-width)' : 'var(--sidebar-width-icon)';
  const marginLeft = isMobile ? 0 : currentSidebarWidth;
  const width = isMobile ? '100%' : `calc(100% - ${currentSidebarWidth})`;

  // Determine layout based on route and user role
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAgencyRoute = location.pathname.startsWith('/home/agency');
  const isAdminDashboard = location.pathname === '/admin/dashboard';
  const isAgencyDashboard = location.pathname === '/home/agency/dashboard';
  const isRegularDashboard = location.pathname === '/home';
  const isNewInbox = location.pathname === '/home/new-inbox';
  const isGenUIWorkspace = location.pathname === '/home/genui';
  const isWebsiteBuilderEditor = /^\/home\/website-builder\/sites\/[^/]+\/editor\/[^/]+$/.test(location.pathname);

  // Remove top padding on dashboard pages
  const removeTopPadding = isAdminDashboard || isAgencyDashboard || isRegularDashboard;

  // Admin library builder pages (need same full-height treatment as FlowBuilder)
  const isAdminLibraryBuilder = location.pathname === '/admin/library/flow/builder' ||
    location.pathname === '/admin/library/whatsapp-flow/builder';

  // Remove all padding only for FlowBuilder page (creating/editing flows) to prevent page scrolling
  const isFlowBuilderPage = isAdminLibraryBuilder || (isRegularUser && location.pathname.startsWith('/home/automation/flows/') &&
    (location.pathname.includes('/new') || location.pathname.match(/\/home\/automation\/flows\/[^/]+$/)));
  const isCompactGenUIPage = isFlowBuilderPage || location.pathname === '/home/flows/builder';

  // Full-screen, chrome-less builder: the /home automation flow builder hides
  // the sidebar, top nav, and banners so the canvas owns the entire viewport
  // (easier drag/drop + node configuration). Admin library builders are
  // deliberately EXCLUDED — they keep the standard chrome.
  const isFullscreenBuilder = isFlowBuilderPage && !isAdminLibraryBuilder;

  // Determine if we are on a Kanban Board detail page
  const isKanbanBoardPage = isRegularUser && (
    (location.pathname.startsWith('/home/boards/') && location.pathname.split('/').length > 3)
    // Deals pipeline board owns its own internal (per-column) scrolling, like the
    // lead Kanban. NOT /home/deals/stages (that's a normal scrollable settings page).
    || location.pathname === '/home/deals'
  );

  // Determine if we should use min-h-screen (admin/agency) or h-screen (regular users)
  const useMinHeight = (isAdminRoute && !isAdminLibraryBuilder) || isAgencyRoute || isNewInbox;

  // Enable sound notifications for inbox messages
  useInboxSoundNotification();

  // Update browser tab title with unread conversation count
  const { totalUnreadConversations } = useUnreadMessages();
  React.useEffect(() => {
    const baseTitle = 'WeNext - Enterprise WhatsApp Business Platform';
    document.title = totalUnreadConversations > 0
      ? `(${totalUnreadConversations}) ${baseTitle}`
      : baseTitle;
  }, [totalUnreadConversations]);

  // Check if user has active plan (uses shared helper — accounts for trial_end date)
  const hasActivePlan = isSubscriptionActive(currentSubscription);

  // Trial state — show trial UI when trial_end is set (our trial system, not Razorpay-created)
  const hasTrialDates = currentSubscription?.trial_end && currentSubscription?.trial_start;
  // isTrialing = status is trialing AND trial hasn't actually ended yet
  const isTrialing = currentSubscription?.status === 'trialing' && hasTrialDates && !checkTrialExpired(currentSubscription);
  // isTrialExpired = status is 'expired' with trial dates OR status is still 'trialing' but trial_end is past
  const isTrialExpired = (currentSubscription?.status === 'expired' && hasTrialDates) || checkTrialExpired(currentSubscription);

  // Paid subscription expiry — cancel_at_period_end or already expired without trial dates
  const isPaidExpired = currentSubscription?.status === 'expired' && !hasTrialDates;
  const isPaidExpiring = currentSubscription?.status === 'active' &&
    currentSubscription?.cancel_at_period_end &&
    currentSubscription?.current_period_end;

  // Determine if the expiring/expired subscription is a trial
  const isTrialPlan = !!(isTrialing || isTrialExpired);

  // Calculate days remaining for both trial and paid subscriptions
  const expiryDate = isTrialing && currentSubscription?.trial_end
    ? new Date(currentSubscription.trial_end)
    : isPaidExpiring && currentSubscription?.current_period_end
      ? new Date(currentSubscription.current_period_end)
      : null;
  const daysRemaining = expiryDate
    ? Math.max(0, Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Combined expired state (trial or paid)
  const isSubscriptionExpired = isTrialExpired || isPaidExpired;

  // Treat pending/past_due as having an active plan (payment in progress)
  const isPendingOrPastDue = currentSubscription &&
    (currentSubscription.status === 'pending' || currentSubscription.status === 'past_due');

  // No plan at all (never subscribed, or cancelled/none status — NOT pending/past_due)
  const hasNoPlan = !hasActivePlan && !isSubscriptionExpired && !isPendingOrPastDue;

  // `!isSubscriptionError` is load-bearing: a failed refetch with no cached data
  // would otherwise present as `currentSubscription === undefined` → `hasNoPlan`
  // → false-positive "No Active Plan" banner. Don't accuse the user of being
  // unsubscribed when we can't actually confirm their state.
  const showSubscriptionBanner = isRegularUser && !isFullscreenBuilder && !authLoading && !isSubscriptionLoading && !isSubscriptionError && (
    (isTrialing && daysRemaining <= 5) ||
    (isPaidExpiring && daysRemaining <= 5) ||
    isSubscriptionExpired ||
    hasNoPlan
  );

  // Track banner dismissal so layout adjusts when user closes the banner
  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  // WABA lock banner — selectedAccount is null on landing pages / before BA load
  const { selectedAccount, accounts, isLoading: businessAccountsLoading } = useBusinessAccountContext();
  const isWabaLocked =
    !!selectedAccount && selectedAccount.lock_status === 'LOCKED';
  const [wabaBannerDismissed, setWabaBannerDismissed] = React.useState(false);
  const showWabaLockedBanner = isRegularUser && !isFullscreenBuilder && isWabaLocked && !wabaBannerDismissed;

  // Meta ads token expiry banner. `useAdsSetupStatus` only fires when there's
  // an authed regular user; admin / agency roles skip the fetch via the
  // `enabled` flag in the hook itself. token_status is null/'HEALTHY' for
  // healthy accounts, 'EXPIRING_SOON' for the 7-day warning window, and
  // 'EXPIRED' for the post-expiry state.
  const { data: adsSetupStatus } = useAdsSetupStatus();
  const adsAccount = adsSetupStatus?.account;
  const adsTokenStatus = adsAccount?.token_status;
  const isAdsTokenAttention =
    adsTokenStatus === 'EXPIRING_SOON' || adsTokenStatus === 'EXPIRED';
  const [adsBannerDismissed, setAdsBannerDismissed] = React.useState(false);
  // Reset the dismissal when the status escalates (EXPIRING_SOON → EXPIRED)
  // so a customer who clicked away on the yellow warning still sees the red
  // post-expiry banner.
  const lastAdsStatusRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (adsTokenStatus && lastAdsStatusRef.current !== adsTokenStatus) {
      setAdsBannerDismissed(false);
      lastAdsStatusRef.current = adsTokenStatus;
    }
  }, [adsTokenStatus]);
  const showAdsTokenExpiryBanner =
    isRegularUser && !isFullscreenBuilder && isAdsTokenAttention && !adsBannerDismissed;

  // Connect-Meta banner — a persistent prompt at the top of every content page
  // until the org has connected a WhatsApp Business account (which requires
  // authorizing Meta). Once ANY account exists, Meta is authorized, so the
  // banner disappears. `accounts` is null while the context loads, so we require
  // an actually-loaded empty array to avoid a flash before data arrives.
  const [metaConnectDialogOpen, setMetaConnectDialogOpen] = React.useState(false);

  React.useEffect(() => {
    setBlockingDialog('billing', showBillingDialog);
    return () => setBlockingDialog('billing', false);
  }, [showBillingDialog]);

  React.useEffect(() => {
    setBlockingDialog('meta-connect', metaConnectDialogOpen);
    return () => setBlockingDialog('meta-connect', false);
  }, [metaConnectDialogOpen]);

  const hasBusinessAccount = Array.isArray(accounts) && accounts.length > 0;
  // Immersive full-screen tools (builders / AI workspaces) own their whole
  // viewport — a marketing banner there breaks the layout. The Business
  // Accounts page is the connect flow itself, so the banner is redundant there.
  const isImmersivePage =
    isFullscreenBuilder || isWebsiteBuilderEditor || isFlowBuilderPage || isGenUIWorkspace;
  const showConnectMetaBanner =
    isRegularUser &&
    !isImmersivePage &&
    !isNewInbox &&
    !businessAccountsLoading &&
    Array.isArray(accounts) &&
    !hasBusinessAccount &&
    // The Overview page (/home) renders its own full-bleed ConnectMetaBanner as
    // part of its onboarding layout — don't stack a second one on top.
    location.pathname !== '/home' &&
    // The design-system showcase already displays the banner as a component demo;
    // the live global one on top would be redundant/confusing there.
    location.pathname !== '/home/V3/design-system';

  // Banner height for layout offset (each banner is ~40px when visible)
  const subscriptionBannerHeight = showSubscriptionBanner && !bannerDismissed ? 40 : 0;
  const wabaBannerHeight = showWabaLockedBanner ? 40 : 0;
  const adsTokenBannerHeight = showAdsTokenExpiryBanner ? 40 : 0;
  // The WABA banner sits BELOW the top header (less intrusive than the
  // subscription banner, which gates plan access). So only subscription
  // banner contributes to the "above header" offset that pushes the sidebar
  // + header down. WABA banner takes its own slot below the header and is
  // accounted for via `wabaBannerHeight` in the main-content paddingTop.
  const bannerHeight = subscriptionBannerHeight;
  // Approx top-nav height — matches the existing main-content paddingTop
  // pattern (`pt-[80px] sm:pt-[100px]` = 40px banner buffer + 40-60px header).
  const HEADER_HEIGHT = isMobile ? 60 : 80;

  // The connect-Meta banner is the first child of <main>, so it normally sits at
  // main's padding-top — which reserves a buffer ABOVE the header height, leaving
  // a visible gap. Pull it up so its top lands exactly at the header's bottom
  // (the same anchor the WABA banner uses: subscriptionBannerHeight + HEADER_HEIGHT),
  // flush like the Overview page. main's padding-top for these pages is
  // (isMobile ? 80 : 100) — WABA/ads banners never co-occur (they need a
  // connected account, and this banner only shows when there's none).
  const connectMetaBannerTop =
    subscriptionBannerHeight + HEADER_HEIGHT - (isMobile ? 80 : 100);

  // Initialize sidebar tour for logged-in users with active plan
  const { startTour } = useSidebarTour({
    enabled: !authLoading && !!user && isRegularUser && !!hasActivePlan,
    user,
    hasActivePlan: !!hasActivePlan,
    sidebarState: state,
    setSidebarOpen: setOpen,
  });
  const navigate = useNavigate();

  // Menu-permission route enforcement: when an admin has set custom menu
  // permissions for this member, a section that isn't granted must be
  // unreachable by URL — not just hidden in the sidebar. AppShell renders only
  // after ProtectedRoute has waited for the profile, so `menu_permissions` is
  // loaded here. Role gates (RoleRoute) still apply on top of this; this is the
  // per-section gate. Runs for regular users in the /home area only.
  const menuPerms = (user as any)?.menu_permissions;
  const inHomeArea = location.pathname === '/home' || location.pathname.startsWith('/home/');
  if (
    isRegularUser &&
    Array.isArray(menuPerms) &&
    inHomeArea &&
    !isAgencyRoute &&
    !isMenuRouteAllowed(location.pathname, menuPerms)
  ) {
    const target = firstGrantedPath(menuPerms);
    // Skip when target === current to avoid a redirect loop (e.g. a member with
    // no granted sections lands on the dashboard rather than bouncing forever).
    if (target !== location.pathname) {
      return <Navigate to={target} replace />;
    }
  }

  return (
    <div className="w-full h-screen bg-background relative">
      {/* Sidebar - offset by banner height when banner is visible */}
      {!isWebsiteBuilderEditor && !isFullscreenBuilder && (
        <div
          style={{
            marginTop: bannerHeight ? `${bannerHeight}px` : '0',
          }}
        >
          <AppSidebar />
        </div>
      )}

      {/* Top Navigation - Positioned below banner */}
      {!isWebsiteBuilderEditor && !isFullscreenBuilder && (
        <div
          className="fixed right-0 z-50 pointer-events-auto transition-[left,top] duration-200 ease-smooth"
          style={{
            left: marginLeft,
            top: bannerHeight
          }}
        >
          <TopNavigation onStartTour={startTour} onOpenBilling={() => setShowBillingDialog(true)} />
        </div>
      )}

      {/* Main Content */}
      <main
        id="app-main-scroll"
        className={[
          'bg-gradient-subtle relative z-0 transition-[left,width] duration-200 ease-smooth',
          isWebsiteBuilderEditor
            ? 'h-screen overflow-hidden p-0 m-0'
            : isFlowBuilderPage
              ? 'h-screen overflow-hidden' // Full height, no padding for FlowBuilder page only - FlowBuilder handles its own padding
              : isGenUIWorkspace
                ? 'h-screen overflow-hidden'
                : isKanbanBoardPage
                  ? 'h-screen overflow-hidden pt-[60px] sm:pt-[80px] mt-[40px] sm:mt-0' // Full height, no scroll on main, but keep header visible
                  : useMinHeight
                    ? !removeTopPadding
                      ? cn(
                        "min-h-screen pt-[80px] sm:pt-[100px] pb-4 sm:pb-6 lg:pb-8",
                        !isNewInbox && "px-4 sm:px-6 lg:px-8"
                      )
                      : 'min-h-screen pt-[60px] sm:pt-[60px]'
                    : !removeTopPadding
                      ? cn(
                        "h-screen overflow-y-auto scrollbar-hide pt-[80px] sm:pt-[100px] pb-4 sm:pb-6 lg:pb-8 mt-[40px] sm:mt-0",
                        !isNewInbox && "px-4 sm:px-6 lg:px-8"
                      )
                      : 'h-screen overflow-y-auto scrollbar-hide pt-[60px] sm:pt-[60px] mt-[40px] sm:mt-0'
        ].join(' ')}
        style={{
          left: (isWebsiteBuilderEditor || isFullscreenBuilder) ? 0 : marginLeft,
          width: (isWebsiteBuilderEditor || isFullscreenBuilder) ? '100%' : width,
          position: 'absolute',
          top: 0, // Push content down by announcement height
          right: 0,
          // Calculate padding top to account for header (+ WABA banner when
          // visible — it sits below the header and would otherwise overlap
          // the first page heading).
          paddingTop: isWebsiteBuilderEditor
            ? 0
            : isFlowBuilderPage
              ? 0
              : isGenUIWorkspace
                ? (isMobile ? 60 : 60) + wabaBannerHeight + adsTokenBannerHeight
                : (!removeTopPadding
                  ? (isMobile ? 80 : 100) + wabaBannerHeight + adsTokenBannerHeight
                  : (isMobile ? 60 : 60) + wabaBannerHeight + adsTokenBannerHeight)
        }}
      >
        {/* Full-bleed at the top of the content area (flush edge-to-edge, square
            corners) to match the Overview page's banner. The negative x-margins
            cancel <main>'s horizontal padding; the negative top margin pulls it
            up flush against the header (main's padding-top otherwise leaves a
            gap). */}
        {showConnectMetaBanner && (
          <div
            className="-mx-4 mb-4 sm:-mx-6 sm:mb-6 lg:-mx-8"
            style={{ marginTop: connectMetaBannerTop }}
          >
            <ConnectMetaBanner
              connected={false}
              onConnect={() => setMetaConnectDialogOpen(true)}
            />
          </div>
        )}
        {/* Remounts the page subtree + clears URL params/transient storage on
            business-account switch so filters never leak across accounts.
            Shell chrome (sidebar, top nav, banners) is outside the boundary
            and keeps its state. */}
        <AccountScopeBoundary>{children}</AccountScopeBoundary>
      </main>

      {/* Subscription Banner - Shows when trial or paid plan is expiring soon or expired */}
      {showSubscriptionBanner && (
        <div className="fixed top-0 left-0 right-0 z-[70]">
          <TrialBanner
            daysRemaining={daysRemaining}
            isExpired={!!isSubscriptionExpired || !!hasNoPlan}
            isTrialPlan={isTrialPlan}
            hasNoPlan={!!hasNoPlan}
            onDismiss={() => setBannerDismissed(true)}
          />
        </div>
      )}

      {/* WABA Locked Banner — sits BELOW the top header (less intrusive than
          the subscription banner). z-index below the header (50) so the
          header sits visually on top if anything ever overlaps. */}
      {showWabaLockedBanner && (
        <div
          className="fixed left-0 right-0 z-[40] transition-[left,width] duration-200 ease-smooth"
          style={{
            top: subscriptionBannerHeight + HEADER_HEIGHT,
            left: isMobile ? 0 : marginLeft,
            width: isMobile ? '100%' : width,
          }}
        >
          <WabaLockedBanner
            accountName={selectedAccount?.name}
            phoneNumber={selectedAccount?.phone_number}
            onDismiss={() => setWabaBannerDismissed(true)}
          />
        </div>
      )}

      {/* Meta ads token-expiry banner — stacked below the WABA-lock banner
          (if both are showing). Same z-index tier; header (z-50) stays on top. */}
      {showAdsTokenExpiryBanner && (
        <div
          className="fixed left-0 right-0 z-[40] transition-[left,width] duration-200 ease-smooth"
          style={{
            top: subscriptionBannerHeight + HEADER_HEIGHT + wabaBannerHeight,
            left: isMobile ? 0 : marginLeft,
            width: isMobile ? '100%' : width,
          }}
        >
          <MetaTokenExpiryBanner
            tokenStatus={adsTokenStatus as 'EXPIRING_SOON' | 'EXPIRED'}
            tokenExpiry={adsAccount?.token_expiry}
            adAccountName={adsAccount?.ad_account_name}
            onDismiss={() => setAdsBannerDismissed(true)}
          />
        </div>
      )}

      {/* Subscription expired warning is now handled by AppSidebar dialog — no full-screen blocker */}

      {/* Billing Dialog - Only for regular users */}
      {isRegularUser && (
        <>
          <BillingDialog open={showBillingDialog} onOpenChange={setShowBillingDialog} />
          {/* <OnboardingChecklist /> */}
        </>
      )}

      {/* Connect-Meta flow launched from the top-of-page ConnectMetaBanner.
          Uses the same "Connect once, activate all" modal as the Overview page
          so the CTA is consistent everywhere. Regular users only. */}
      {isRegularUser && (
        <ConnectMetaModal
          open={metaConnectDialogOpen}
          onClose={() => setMetaConnectDialogOpen(false)}
        />
      )}

      {/* Echo AI Chat Widget — opened by the "Ask AI" header button */}
      <ChatWidget />

      {/* GenUI Aria AI Assistant */}
      {isCompactGenUIPage && <GenUIChatbox />}

      {/* Push Notifications — silently reconnects token when user is logged in */}
      <PushNotificationManager />

      <PWAInstallPrompt />

      {isRegularUser && <WhatsNewModal />}

      {/* WhatsApp calling feature disabled
      {isRegularUser && (
        <WhatsAppCallingManager />
      )} */}
    </div>
  );
};

// WhatsApp calling feature disabled
// const WhatsAppCallingManager: React.FC = () => {
//   ...
// };

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  // Calculate initial sidebar state based on current pathname to prevent layout shift
  // Check if we're on a FlowBuilder page synchronously (before first render)
  const getInitialSidebarState = () => {
    if (typeof window === 'undefined') return true;
    const pathname = window.location.pathname;
    const isAdminLibraryBuilder = pathname === '/admin/library/flow/builder' || pathname === '/admin/library/whatsapp-flow/builder';
    const isFlowBuilderPage = isAdminLibraryBuilder || (pathname.startsWith('/home/automation/flows/') &&
      (pathname.includes('/new') || pathname.match(/\/home\/automation\/flows\/[^/]+$/)));
    // For admin and agency routes, always start expanded (except library builders). For regular users, collapse on FlowBuilder pages
    const isAdminRoute = pathname.startsWith('/admin');
    const isAgencyRoute = pathname.startsWith('/home/agency');
    if ((isAdminRoute && !isAdminLibraryBuilder) || isAgencyRoute) return true;
    return !isFlowBuilderPage; // Collapsed on FlowBuilder pages, expanded elsewhere
  };

  return (
    <SidebarProvider
      defaultOpen={getInitialSidebarState()}
      style={{ ['--sidebar-width' as any]: '280px', ['--sidebar-width-icon' as any]: '56px' }}
    >
      <GenUIFullProvider>
        <AiChatProvider>
          <GenUIProvider>
            <AppShellContent>{children}</AppShellContent>
          </GenUIProvider>
        </AiChatProvider>
      </GenUIFullProvider>
    </SidebarProvider>
  );
};
